/**
 * Port von `IwmDialogRechnungszuordnung.razor`.
 *
 * Bringt bewusst **keinen** eigenen `Dialog` mit: Der Altclient bindet die Komponente als
 * Inhalt in den Dialog der Rechnungs-View ein.
 */

import { useState } from 'react';

import { receiptAssignmentApi } from '@/api/services/receiptAssignmentApi';
import { ContainerDropdown } from '@/components/inputs/ContainerDropdown';
import { Button, NumericTextBox, ProgressButton } from '@/components/ui';
import { createReceiptAssignment, type ReceiptAssignment } from '@/types/entities';

export interface ReceiptAssignmentDialogProps {
  viewId: number;
  rechnungId: number;
}

export function ReceiptAssignmentDialog(props: ReceiptAssignmentDialogProps) {
  const { rechnungId, viewId } = props;

  // Port von `OnInitializedAsync` → `New()`: genau ein Eintrag.
  const [models, setModels] = useState<ReceiptAssignment[]>(() => [
    { ...createReceiptAssignment(), RechnungId: rechnungId },
  ]);
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);

  const valid = models.every((model) => model.ContainerId > 0);

  const addModel = () => {
    setModels((current) => [...current, { ...createReceiptAssignment(), RechnungId: rechnungId }]);
  };

  const updateModel = (index: number, patch: Partial<ReceiptAssignment>) => {
    setModels((current) =>
      current.map((model, position) => (position === index ? { ...model, ...patch } : model)),
    );
  };

  /** Port von `Save()` — vorhandene Einträge werden zusätzlich noch einmal angelegt. */
  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      for (const model of models) {
        if (model.Id > 0) await receiptAssignmentApi.put(viewId, model.Id, model);
        await receiptAssignmentApi.create(viewId, model);
        await receiptAssignmentApi.recalc(viewId, model.ContainerId);
      }
      setSaving(false);
      setModels([]);
      setSaveOk(true);
    } catch (error) {
      // Der Altclient lässt `Saving` hier stehen; das würde den Speichern-Knopf dauerhaft
      // sperren, deshalb wird das Kennzeichen zurückgesetzt.
      setSaving(false);
      console.error('Error Receipt assignment', error);
    }
  };

  if (saveOk) {
    return <div className="e-success">Receipt assignment created successfully.</div>;
  }

  if (rechnungId <= 0) {
    return <div className="e-error">Bitte wählen Sie eine Receipt aus</div>;
  }

  return (
    <>
      <div className="row">
        <div className="col-lg-12">
          {models.map((model, index) => (
            <div className="row" key={index}>
              <div className="col-lg-6">
                <NumericTextBox
                  value={model.BetragContainerNetto}
                  onChange={(value) => {
                    updateModel(index, { BetragContainerNetto: value });
                  }}
                  format="N2"
                  label="Betrag Container Netto"
                />
              </div>
              <div className="col-lg-6">
                <ContainerDropdown
                  value={model.ContainerId}
                  onChange={(value) => {
                    updateModel(index, { ContainerId: value ?? 0 });
                  }}
                  viewId={viewId}
                  label="Container"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="row" style={{ marginTop: '1rem', marginBottom: '1rem', float: 'right' }}>
        <div className="col-lg-12">
          <Button content="Assign to Container" onClick={addModel} />
          <ProgressButton
            content="Save"
            primary
            type="submit"
            progress={saving}
            disabled={!valid}
            onClick={save}
          />
        </div>
      </div>
    </>
  );
}

export default ReceiptAssignmentDialog;
